import React, { useState, useEffect } from 'react';
import deviceManager from '../utils/deviceManager';
import enhancedOfflineStorage from '../utils/enhancedOfflineStorage';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ pending: 0, failed: 0 });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Initialize device status and refresh from Firebase
    const initializeDeviceStatus = async () => {
      try {
        // Get fresh device info from Firebase
        const deviceInfo = await deviceManager.getDeviceInfo();
        const status = deviceManager.getDeviceStatus();
        setDeviceStatus(status);
        console.log('OfflineIndicator - Device Status:', status);
      } catch (error) {
        console.error('Error initializing device status:', error);
        const status = deviceManager.getDeviceStatus();
        setDeviceStatus(status);
      }
    };

    initializeDeviceStatus();

    // Set up network monitoring
    const handleOnline = () => {
      setIsOnline(true);
      updateSyncStatus();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Update sync status periodically
    const interval = setInterval(updateSyncStatus, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const updateSyncStatus = async () => {
    try {
      if (deviceStatus?.companyId) {
        const pending = await enhancedOfflineStorage.getSyncQueue({ 
          status: 'pending',
          companyId: deviceStatus.companyId 
        });
        const failed = await enhancedOfflineStorage.getSyncQueue({ 
          status: 'failed',
          companyId: deviceStatus.companyId 
        });
        
        setSyncStatus({
          pending: pending.length,
          failed: failed.length
        });
      }
    } catch (error) {
      console.error('Error updating sync status:', error);
    }
  };

  const getStatusColor = () => {
    if (!isOnline) return 'bg-red-500';
    if (syncStatus.failed > 0) return 'bg-yellow-500';
    if (syncStatus.pending > 0) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (syncStatus.failed > 0) return 'Sync Issues';
    if (syncStatus.pending > 0) return 'Syncing';
    return 'Online';
  };

  const getDeviceTypeLabel = () => {
    if (!deviceStatus) return '';
    return deviceStatus.deviceType === 'offline' ? 'Offline Device' : 'Online Device';
  };

  const getDeviceStatusColor = () => {
    if (!deviceStatus) return 'text-gray-500';
    if (deviceStatus.isApproved) return 'text-green-600';
    return 'text-yellow-600';
  };

  if (!deviceStatus) return null;

  return (
    <div className="relative">
      {/* Main Indicator - Thin Line Style */}
      <div 
        className={`fixed top-0 right-0 z-50 w-1 h-16 ${isOnline ? 'bg-green-500' : 'bg-red-500'} cursor-pointer transition-all duration-300 hover:opacity-80`}
        onClick={() => setShowDetails(!showDetails)}
        title={getStatusText()}
      >
        {/* Sync indicator dot */}
        {(syncStatus.pending > 0 || syncStatus.failed > 0) && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {syncStatus.pending + syncStatus.failed}
            </span>
          </div>
        )}
      </div>

      {/* Details Panel - Mobile Friendly */}
      {showDetails && (
        <div className="fixed top-20 right-2 sm:right-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 sm:p-4 w-72 sm:w-80">
          <div className="space-y-3">
            {/* Device Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Device Information</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="text-gray-900">{deviceStatus.deviceName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className={`font-medium ${getDeviceStatusColor()}`}>
                    {getDeviceTypeLabel()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${getDeviceStatusColor()}`}>
                    {deviceStatus.isApproved ? 'Approved' : 'Pending Approval'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Connection:</span>
                  <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>

            {/* Sync Status */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Sync Status</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pending:</span>
                  <span className="text-blue-600 font-medium">{syncStatus.pending}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Failed:</span>
                  <span className="text-red-600 font-medium">{syncStatus.failed}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-2 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  onClick={async () => {
                    if (isOnline) {
                      try {
                        console.log('Manual sync triggered...');
                        // Trigger full data sync
                        await enhancedOfflineStorage.syncCompanyData();
                        console.log('Manual sync completed');
                        // Also sync any pending data
                        await deviceManager.syncPendingData();
                      } catch (error) {
                        console.error('Error during manual sync:', error);
                      }
                    }
                  }}
                  disabled={!isOnline}
                  className="flex-1 bg-blue-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded text-xs sm:text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {deviceStatus?.isApproved ? 'Force Sync' : 'Sync Now'}
                </button>
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-2 py-1 sm:px-3 sm:py-1.5 rounded text-xs sm:text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Offline Mode Info */}
            {!isOnline && deviceStatus.deviceType === 'offline' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  You're working offline. All changes will be synced when you're back online.
                </p>
              </div>
            )}

            {/* Online Mode Info */}
            {isOnline && deviceStatus.deviceType === 'online' && (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-xs text-green-800">
                  You're working online. All changes are synced in real-time.
                </p>
              </div>
            )}

            {/* Pending Approval Info */}
            {!deviceStatus.isApproved && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  Your device is pending approval for offline use. Contact your administrator.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {showDetails && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowDetails(false)}
        ></div>
      )}
    </div>
  );
};

export default OfflineIndicator;
